/** Result shape returned by `makeApiCall` helpers in this codebase. */
export type ApiCallResult<T = unknown> =
  | { success: true; data: T }
  | {
      success: false;
      data: { message?: string; [key: string]: unknown };
      errorStatus?: number;
    }
  | {
      data: {
        title?: string;
        subtitle?: string;
        message?: string;
        errorStatus?: number;
      };
    };

export function isApiSuccess(
  callResult: unknown,
): callResult is { success: true; data: unknown } {
  return (
    typeof callResult === "object" &&
    callResult != null &&
    "success" in callResult &&
    (callResult as { success?: boolean }).success === true
  );
}

export function isUnauthorized(callResult: unknown): boolean {
  return (
    typeof callResult === "object" &&
    callResult != null &&
    "errorStatus" in callResult &&
    (callResult as { errorStatus?: number }).errorStatus === 401
  );
}

export function getApiErrorMessage(
  callResult: unknown,
  fallback: string,
): string {
  if (!callResult || typeof callResult !== "object") return fallback;
  const data = (callResult as { data?: { message?: string } }).data;
  return data?.message ?? fallback;
}

export interface HandlePoApiResultOptions<T> {
  logout: () => void;
  onError: (message: string) => void;
  errorMessage?: string;
  onSuccess?: (data: T) => void | boolean | Promise<void | boolean>;
}

/** Normalize success / 401 / error handling for purchase order API calls. */
export async function handlePoApiResult<T = unknown>(
  callResult: unknown,
  {
    logout,
    onError,
    errorMessage = "Request failed.",
    onSuccess,
  }: HandlePoApiResultOptions<T>,
): Promise<boolean> {
  if (isApiSuccess(callResult)) {
    if (onSuccess) {
      const result = await onSuccess(callResult.data as T);
      return result !== false;
    }
    return true;
  }

  if (isUnauthorized(callResult)) {
    logout();
    return false;
  }

  onError(getApiErrorMessage(callResult, errorMessage));
  return false;
}
